'use client';

import React, { useState, forwardRef, useLayoutEffect } from 'react';
import classnames from 'classnames';

import Box from '../Box/Box';
import Typography from '../Typography/Typography';

import styles from './TextArea.module.scss';

type TextAreaSize = 'small' | 'medium' | 'large';
type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';

interface ValidationResponse {
  validationType?: 'success' | 'warning' | 'error';
  message?: string;
}

export interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
> {
  name: string;
  register?: (name: string) => Record<string, unknown>;
  theme?: Theme;
  size?: TextAreaSize;
  label?: string;
  helperText?: string;
  placeholder?: string;
  className?: string;
  direction?: Direction;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  required?: boolean;
  disabled?: boolean;
  controlled?: boolean;
  rows?: number;
  maxLength?: number;
  showCharCount?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      name,
      register,
      theme = 'dark',
      size = 'medium',
      label,
      helperText,
      placeholder,
      className = '',
      direction = 'ltr',
      validationResponse,
      showValidation = true,
      required = false,
      disabled = false,
      controlled = true,
      rows = 4,
      maxLength,
      showCharCount = false,
      resize = 'vertical',
    },
    ref
  ) => {
    const [value, setValue] = useState('');

    // In uncontrolled mode, RHF sets the textarea value via its ref callback
    // during the commit phase (before useLayoutEffect). Read it here to
    // initialise the char-count state for pre-populated draft fields.
    useLayoutEffect(() => {
      if (!controlled) {
        const el = document.getElementById(name);
        if (el instanceof HTMLTextAreaElement && el.value) {
          setValue(el.value);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const registeredProps = register ? register(name) : {};
    const rhfOnChange = registeredProps.onChange as
      | React.ChangeEventHandler<HTMLTextAreaElement>
      | undefined;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      // In uncontrolled mode propagate to RHF so form state stays in sync.
      if (!controlled && rhfOnChange) {
        rhfOnChange(e);
      }
    };

    const charCount = value.length;
    const showCount = showCharCount || maxLength;

    return (
      <Box className={classnames(styles.textArea, className)}>
        {(label || helperText) && (
          <Box mb={4}>
            {label && (
              <label htmlFor={name}>
                <Typography
                  className={styles.label}
                  variant="label"
                  size="small"
                  direction={direction}
                  theme={theme}
                  color="gray-3"
                >
                  {label}
                  {required && '*'}
                </Typography>
              </label>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <Box
          className={classnames(
            styles.textAreaWrapper,
            styles[`theme-${theme}`],
            styles[`validation-${validationResponse?.validationType}`],
            disabled && styles.disabled
          )}
        >
          <textarea
            ref={ref}
            className={classnames(
              styles.textAreaField,
              styles[size],
              styles[direction],
              styles[`resize-${resize}`]
            )}
            placeholder={placeholder}
            {...(controlled ? { value: value } : {})}
            required={required}
            disabled={disabled}
            id={name}
            name={name}
            rows={rows}
            maxLength={maxLength}
            aria-describedby={`${name}-validation-message`}
            {...registeredProps}
            onChange={handleChange}
          />
        </Box>
        {showCount && (
          <Box mt={4} className={styles.charCount}>
            <Typography
              variant="body"
              size="extraSmall"
              theme={theme}
              color="gray-5"
              direction={direction}
            >
              {charCount}
              {maxLength && `/${maxLength}`}
            </Typography>
          </Box>
        )}
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={classnames(
                  styles.validationMessage,
                  styles[`theme-${theme}`],
                  validationResponse?.validationType &&
                    styles[`validation-${validationResponse.validationType}`]
                )}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                id={`${name}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
